import {
  assertArray,
  assertNonEmptyString,
  assertRecord,
  hasOwn,
  rejectUnknownKeys,
  validateEnum,
  validateMetricArray,
  validateOptionalBoolean,
  validateOptionalCallback,
  validateOptionalFiniteNumber,
  validateOptionalString,
  validateTone,
} from './elementFields.validation';

/** A unit header prints the unit's own symbol (m³, m²), never an ASCII stand-in. */
function rejectAsciiUnit(text: string, label: string): void {
  if (/\bm[23]\b/.test(text)) {
    throw new Error(
      `Invalid descriptor: ${label} ("${text}") spells a unit as m2/m3; use the symbol (m², m³).`,
    );
  }
}

export function validateRankedTableInsight(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    [
      'objective',
      'lensLabel',
      'lenses',
      'activeLensId',
      'onLensChange',
      'columns',
      'rows',
      'previewLimit',
      'showAllLabel',
      'showLessLabel',
      'selectedRowId',
      'selectionControlled',
      'onRowSelect',
      'insights',
      'insightsLabel',
      'assumptions',
      'unavailableReason',
      'emptyLabel',
      'tableCaption',
      'testId',
    ],
    label,
  );
  assertNonEmptyString(value.objective, `${label}.objective`);
  [
    'lensLabel',
    'activeLensId',
    'showAllLabel',
    'showLessLabel',
    'selectedRowId',
    'insightsLabel',
    'assumptions',
    'unavailableReason',
    'emptyLabel',
    'tableCaption',
    'testId',
  ].forEach((key) => validateOptionalString(value, key, label));
  validateOptionalBoolean(value, 'selectionControlled', label);
  ['onLensChange', 'onRowSelect'].forEach((key) => validateOptionalCallback(value, key, label));
  validateOptionalFiniteNumber(value, 'previewLimit', label);
  if (value.lenses !== undefined) {
    assertArray(value.lenses, `${label}.lenses`);
    value.lenses.forEach((lens, index) => {
      const itemLabel = `${label}.lenses[${index}]`;
      assertRecord(lens, itemLabel);
      rejectUnknownKeys(lens, ['id', 'label', 'disabled'], itemLabel);
      assertNonEmptyString(lens.id, `${itemLabel}.id`);
      assertNonEmptyString(lens.label, `${itemLabel}.label`);
      validateOptionalBoolean(lens, 'disabled', itemLabel);
    });
  }
  assertArray(value.columns, `${label}.columns`);
  value.columns.forEach((column, index) => {
    const itemLabel = `${label}.columns[${index}]`;
    assertRecord(column, itemLabel);
    rejectUnknownKeys(column, ['key', 'header', 'role', 'numeric'], itemLabel);
    assertNonEmptyString(column.key, `${itemLabel}.key`);
    assertNonEmptyString(column.header, `${itemLabel}.header`);
    rejectAsciiUnit(column.header, `${itemLabel}.header`);
    validateEnum(
      column.role,
      ['label', 'numeric', 'money', 'delta', 'status', 'source', 'metadata'],
      `${itemLabel}.role`,
      true,
    );
    validateOptionalBoolean(column, 'numeric', itemLabel);
  });
  assertArray(value.rows, `${label}.rows`);
  value.rows.forEach((row, index) => {
    const itemLabel = `${label}.rows[${index}]`;
    assertRecord(row, itemLabel);
    rejectUnknownKeys(row, ['id', 'values', 'ariaLabel', 'testId'], itemLabel);
    assertNonEmptyString(row.id, `${itemLabel}.id`);
    assertRecord(row.values, `${itemLabel}.values`);
    Object.entries(row.values).forEach(([key, entry]) => {
      if (
        !key.trim() ||
        (entry !== null &&
          entry !== undefined &&
          typeof entry !== 'string' &&
          typeof entry !== 'number')
      ) {
        throw new Error(`Invalid descriptor: ${itemLabel}.values.${key} is invalid.`);
      }
    });
    ['ariaLabel', 'testId'].forEach((key) => validateOptionalString(row, key, itemLabel));
  });
  assertArray(value.insights, `${label}.insights`);
  value.insights.forEach((insight, index) => {
    const itemLabel = `${label}.insights[${index}]`;
    assertRecord(insight, itemLabel);
    rejectUnknownKeys(insight, ['id', 'title', 'body', 'tone'], itemLabel);
    ['id', 'title', 'body'].forEach((key) =>
      assertNonEmptyString(insight[key], `${itemLabel}.${key}`),
    );
    validateTone(insight, itemLabel);
  });
}

/**
 * A table cell: a scalar, or one structured cell of the table vocabulary
 * (signed value, species swatch by code, operation tag).
 */
export function validateTableCellValue(value: unknown, label: string): void {
  if (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return;
  }
  assertRecord(value, label);
  if (value.kind === 'signed') {
    rejectUnknownKeys(value, ['kind', 'value', 'tone', 'format', 'precision', 'emphasis'], label);
    if (
      value.value !== null &&
      typeof value.value !== 'string' &&
      typeof value.value !== 'number'
    ) {
      throw new Error(`Invalid descriptor: ${label}.value must be text, a number or null.`);
    }
    validateEnum(value.tone, ['default', 'positive', 'negative', 'subtle'], `${label}.tone`);
    validateEnum(value.format, ['currency', 'number', 'volume', 'text'], `${label}.format`, true);
    validateOptionalFiniteNumber(value, 'precision', label);
    validateEnum(value.emphasis, ['normal', 'strong'], `${label}.emphasis`, true);
    return;
  }
  if (value.kind === 'species') {
    rejectUnknownKeys(value, ['kind', 'label', 'speciesCode'], label);
    assertNonEmptyString(value.label, `${label}.label`);
    if (value.speciesCode !== null && !Number.isInteger(value.speciesCode)) {
      throw new Error(`Invalid descriptor: ${label}.speciesCode must be a species code or null.`);
    }
    return;
  }
  if (value.kind === 'operation') {
    rejectUnknownKeys(value, ['kind', 'label', 'operation'], label);
    assertNonEmptyString(value.label, `${label}.label`);
    validateEnum(
      value.operation,
      ['thinning', 'seed-tree', 'clearcut', 'planting', 'tending', 'other'],
      `${label}.operation`,
    );
    return;
  }
  throw new Error(
    `Invalid descriptor: ${label} must be a scalar or a signed, species or operation cell.`,
  );
}

export function validateTable(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    [
      'caption',
      'columns',
      'rows',
      'controls',
      'previewLimit',
      'showAllLabel',
      'showLessLabel',
      'emptyText',
      'tableTestId',
      'rowTestId',
      'rowAriaLabel',
      'rowActions',
      'actionHeader',
      'actionLabel',
      'actionOverflowLabel',
      'selectedRowId',
      'onRowSelect',
      'locale',
      'layout',
      'mobileLayout',
      'emptyCellLabel',
    ],
    label,
  );
  assertNonEmptyString(value.caption, `${label}.caption`);
  [
    'showAllLabel',
    'showLessLabel',
    'emptyText',
    'tableTestId',
    'actionHeader',
    'actionLabel',
    'actionOverflowLabel',
    'selectedRowId',
    'locale',
    'emptyCellLabel',
  ].forEach((key) => validateOptionalString(value, key, label));
  validateEnum(value.layout, ['standard', 'operational'], `${label}.layout`, true);
  validateEnum(value.mobileLayout, ['table', 'labelled-cells'], `${label}.mobileLayout`, true);
  ['rowTestId', 'rowAriaLabel', 'rowActions', 'onRowSelect'].forEach((key) =>
    validateOptionalCallback(value, key, label),
  );
  // The action column prints its header on desktop; a missing header has no
  // locale-neutral substitute, so a table with row actions must name it.
  if (hasOwn(value, 'rowActions') && !hasOwn(value, 'actionHeader')) {
    throw new Error(`Invalid descriptor: ${label}.actionHeader is required when rowActions is set.`);
  }
  validateOptionalFiniteNumber(value, 'previewLimit', label);
  assertArray(value.columns, `${label}.columns`);
  value.columns.forEach((column, index) => {
    const itemLabel = `${label}.columns[${index}]`;
    assertRecord(column, itemLabel);
    rejectUnknownKeys(
      column,
      ['key', 'header', 'numeric', 'sortable', 'role', 'mobileLabel', 'mobileHidden'],
      itemLabel,
    );
    assertNonEmptyString(column.key, `${itemLabel}.key`);
    assertNonEmptyString(column.header, `${itemLabel}.header`);
    rejectAsciiUnit(column.header, `${itemLabel}.header`);
    validateOptionalString(column, 'mobileLabel', itemLabel);
    if (typeof column.mobileLabel === 'string') {
      rejectAsciiUnit(column.mobileLabel, `${itemLabel}.mobileLabel`);
    }
    ['numeric', 'sortable', 'mobileHidden'].forEach((key) =>
      validateOptionalBoolean(column, key, itemLabel),
    );
    validateEnum(
      column.role,
      ['label', 'numeric', 'money', 'delta', 'status', 'source', 'metadata'],
      `${itemLabel}.role`,
      true,
    );
  });
  assertArray(value.rows, `${label}.rows`);
  value.rows.forEach((row, index) => {
    const itemLabel = `${label}.rows[${index}]`;
    assertRecord(row, itemLabel);
    assertNonEmptyString(row.key, `${itemLabel}.key`);
    Object.entries(row).forEach(([key, entry]) => {
      if (!key.trim()) throw new Error(`Invalid descriptor: ${itemLabel}.${key} is invalid.`);
      validateTableCellValue(entry, `${itemLabel}.${key}`);
    });
  });
  if (
    value.selectedRowId !== undefined &&
    !value.rows.some(
      (row) =>
        row &&
        typeof row === 'object' &&
        (row as Record<string, unknown>).key === value.selectedRowId,
    )
  ) {
    throw new Error(`Invalid descriptor: ${label}.selectedRowId must reference a served row.`);
  }
  if (value.controls !== undefined) {
    assertArray(value.controls, `${label}.controls`);
    value.controls.forEach((control, index) => {
      const itemLabel = `${label}.controls[${index}]`;
      assertRecord(control, itemLabel);
      if (control.kind === 'selector') {
        rejectUnknownKeys(
          control,
          ['kind', 'id', 'label', 'value', 'variant', 'options', 'onChange', 'testId'],
          itemLabel,
        );
        ['id', 'label', 'value'].forEach((key) =>
          assertNonEmptyString(control[key], `${itemLabel}.${key}`),
        );
        validateEnum(control.variant, ['segmented', 'radio'], `${itemLabel}.variant`, true);
        assertArray(control.options, `${itemLabel}.options`);
        control.options.forEach((option, optionIndex) => {
          const optionLabel = `${itemLabel}.options[${optionIndex}]`;
          assertRecord(option, optionLabel);
          rejectUnknownKeys(option, ['value', 'label', 'disabled', 'testId'], optionLabel);
          assertNonEmptyString(option.value, `${optionLabel}.value`);
          assertNonEmptyString(option.label, `${optionLabel}.label`);
          validateOptionalBoolean(option, 'disabled', optionLabel);
          validateOptionalString(option, 'testId', optionLabel);
        });
      } else if (control.kind === 'toggle') {
        rejectUnknownKeys(
          control,
          ['kind', 'id', 'label', 'checked', 'onChange', 'testId'],
          itemLabel,
        );
        assertNonEmptyString(control.id, `${itemLabel}.id`);
        assertNonEmptyString(control.label, `${itemLabel}.label`);
        if (typeof control.checked !== 'boolean')
          throw new Error(`Invalid descriptor: ${itemLabel}.checked must be a boolean.`);
      } else throw new Error(`Invalid descriptor: ${itemLabel}.kind is not registered.`);
      validateOptionalCallback(control, 'onChange', itemLabel);
      validateOptionalString(control, 'testId', itemLabel);
    });
  }
}

export function validateComparisonMatrix(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    [
      'presentation',
      'items',
      'groups',
      'activeGroupId',
      'groupLabel',
      'criterionLabel',
      'drilledItemId',
      'onGroupChange',
      'onDrillItem',
      'emptyLabel',
      'testId',
    ],
    label,
  );
  validateEnum(value.presentation, ['story-table', 'table-only'], `${label}.presentation`, true);
  [
    'activeGroupId',
    'groupLabel',
    'criterionLabel',
    'drilledItemId',
    'emptyLabel',
    'testId',
  ].forEach((key) => validateOptionalString(value, key, label));
  ['onGroupChange', 'onDrillItem'].forEach((key) => validateOptionalCallback(value, key, label));
  assertArray(value.items, `${label}.items`);
  value.items.forEach((item, index) => {
    const itemLabel = `${label}.items[${index}]`;
    assertRecord(item, itemLabel);
    rejectUnknownKeys(
      item,
      ['id', 'label', 'summary', 'status', 'tone', 'metrics', 'testId'],
      itemLabel,
    );
    assertNonEmptyString(item.id, `${itemLabel}.id`);
    assertNonEmptyString(item.label, `${itemLabel}.label`);
    ['summary', 'status', 'testId'].forEach((key) => validateOptionalString(item, key, itemLabel));
    validateTone(item, itemLabel);
    if (item.metrics !== undefined) validateMetricArray(item.metrics, `${itemLabel}.metrics`);
  });
  assertArray(value.groups, `${label}.groups`);
  value.groups.forEach((group, index) => {
    const groupLabel = `${label}.groups[${index}]`;
    assertRecord(group, groupLabel);
    rejectUnknownKeys(group, ['id', 'label', 'summary', 'criteria'], groupLabel);
    assertNonEmptyString(group.id, `${groupLabel}.id`);
    assertNonEmptyString(group.label, `${groupLabel}.label`);
    validateOptionalString(group, 'summary', groupLabel);
    assertArray(group.criteria, `${groupLabel}.criteria`);
    group.criteria.forEach((criterion, criterionIndex) => {
      const criterionLabel = `${groupLabel}.criteria[${criterionIndex}]`;
      assertRecord(criterion, criterionLabel);
      rejectUnknownKeys(
        criterion,
        ['key', 'label', 'detail', 'values', 'winnerItemId', 'tone'],
        criterionLabel,
      );
      assertNonEmptyString(criterion.key, `${criterionLabel}.key`);
      assertNonEmptyString(criterion.label, `${criterionLabel}.label`);
      ['detail', 'winnerItemId'].forEach((key) =>
        validateOptionalString(criterion, key, criterionLabel),
      );
      validateTone(criterion, criterionLabel);
      assertRecord(criterion.values, `${criterionLabel}.values`);
      Object.entries(criterion.values).forEach(([key, entry]) =>
        assertNonEmptyString(entry, `${criterionLabel}.values.${key}`),
      );
    });
  });
}
